import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Checkbox, ColorPicker, Modal, Slider, Tooltip, message } from 'antd'
import { AimOutlined } from '@ant-design/icons'
import {
  backgroundEditorSave,
  getActiveAutoProcessTemplate,
  pixelsUrl,
  syncStageToActiveTemplate,
  type AssetChainOut,
  type BackgroundEditorParams,
} from '../../api/client'
import EditorHeader, { confirmDiscardAndClose } from './EditorHeader'
import { submitEditorSave } from './editorSaveManager'
import {
  BG_PRESET_COLOR,
  BG_PRESET_LABEL,
  DEFAULT_SCALE_PCT,
  MAX_SCALE_PCT,
  MIN_SCALE_PCT,
  RATIO_PRESETS,
  clamp,
  computeAlphaBBox,
  defaultGradients,
  hexToRgb,
  isSolidGradients,
  loadHtmlImage,
  migrateBackgroundGradients,
  normalizeCssColorToHex,
  paintBackgroundGradients,
  rgbToHex,
  type AlphaBBox,
  type BackgroundGradientConfig,
  type BgPreset,
  type GradientAxisConfig,
} from './backgroundPresets'
import { getLastPixelLoadTiming, logEditorLoadTiming } from './mediaLoader'
import { MODAL_SURFACE_CLASS, useModalScrollLock } from '../../saas/utils/scrollLock'

type GradientAxisKey = keyof BackgroundGradientConfig

const GRADIENT_AXIS_ORDER: GradientAxisKey[] = ['radial', 'vertical', 'horizontal']

/**
 * Background Editor (色底图编辑器) — a deliberately small, fast tool for
 * placing the transparent cutout on a background for a single 色底图, not a
 * general-purpose image editor. Everything (ratio, background, gradient,
 * subject scale/position) renders live on one <canvas> in the browser;
 * nothing is sent to the backend until the user clicks 保存, at which point
 * the same canvas is exported as a single flattened JPEG that overwrites
 * the chain's one-and-only 色底图 (no history is kept).
 */

interface Props {
  /** Required in asset mode (identifies which chain's 色底图 to overwrite).
   * Not read in template mode — pass the template id here anyway so the
   * open/reload effect keys correctly per template. */
  chainId: string
  filename: string
  transparentUrl: string
  /** Previously-saved editor state (ratio/background/subject placement), if
   * any — restored on open instead of resetting to defaults, so re-opening
   * the editor continues exactly where the user left off. `null`/`undefined`
   * for chains whose 色底图 was produced by the automatic pipeline only, or
   * by an editor session before this was persisted. */
  initialParams?: BackgroundEditorParams | null
  open: boolean
  onClose: () => void
  /** Asset mode (default): called with the updated chain after the backend
   * save succeeds. Ignored when `onSaveOverride` is provided. */
  onSaved?: (chain: AssetChainOut) => void
  /** Template mode: when provided, the exported canvas blob + params are
   * handed to this callback instead of posting to `/chains/:id/editor/...`
   * — the caller (AutoProcessTemplateBar) persists them onto the template
   * and regenerates its preview instead of touching a real asset. Same
   * canvas/render code either way (spec 十: 同一个 BackgroundLayoutEditor). */
  onSaveOverride?: (blob: Blob, params: BackgroundEditorParams) => Promise<void>
  /** Template mode only: replaces the header's "色底图编辑器" title. */
  titlePrefix?: string
}

interface SubjectTransform {
  scalePct: number
  offsetXFrac: number
  offsetYFrac: number
}

const DEFAULT_TRANSFORM: SubjectTransform = { scalePct: DEFAULT_SCALE_PCT, offsetXFrac: 0, offsetYFrac: 0 }

export default function BackgroundEditorModal({
  chainId,
  filename,
  transparentUrl,
  initialParams,
  open,
  onClose,
  onSaved,
  onSaveOverride,
  titlePrefix,
}: Props) {
  const { t } = useTranslation('autoProcess')
  useModalScrollLock(open)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const imgRef = useRef<HTMLImageElement | null>(null)
  const bboxRef = useRef<AlphaBBox | null>(null)
  const [imgLoaded, setImgLoaded] = useState(false)
  const [loadError, setLoadError] = useState('')
  // Editor Load Performance P0 (2026-08): `imgRef` may hold the fast
  // `transparent_proxy` preview (asset mode only) until the full-resolution
  // `transparent` source finishes loading in the background — `doSave`
  // below awaits `fullResPromiseRef` before exporting so a save can never
  // ship the lower-resolution preview source (spec: "Preview optimization
  // 不能降低最终输出分辨率").
  const fullResReadyRef = useRef(false)
  const fullResPromiseRef = useRef<Promise<HTMLImageElement> | null>(null)
  const loadStartRef = useRef(0)
  // Bumped (never read directly) when the background full-res upgrade lands,
  // purely to re-trigger the `draw()` effect below with THIS render's fresh
  // `draw` closure — `imgRef`/`bboxRef` are plain refs, so mutating them
  // alone would never schedule a re-render, and calling a `draw` closed over
  // at effect-registration time risks it being stale w.r.t. ratio/gradients/
  // transform the user changed while the full-res image was still loading.
  const [fullResVersion, setFullResVersion] = useState(0)

  const [ratioKey, setRatioKey] = useState<string>('1:1')
  const ratio = useMemo(() => RATIO_PRESETS.find((r) => r.key === ratioKey) || RATIO_PRESETS[0], [ratioKey])

  const [bgPreset, setBgPreset] = useState<BgPreset>('white')
  const [customColor, setCustomColor] = useState('#ffffff')
  // Always `#rrggbb` — never pass a raw ColorPicker `rgb(...)` string into
  // the gradient renderer (see `normalizeCssColorToHex` docstring).
  const baseColor = bgPreset === 'custom' ? normalizeCssColorToHex(customColor) : BG_PRESET_COLOR[bgPreset]

  const [gradients, setGradients] = useState<BackgroundGradientConfig>(defaultGradients())

  const [transform, setTransform] = useState<SubjectTransform>(DEFAULT_TRANSFORM)
  const [dirty, setDirty] = useState(false)
  const [saveSubmitted, setSaveSubmitted] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  // "保存" vs "保存并同时更新模板" (spec 十) — only relevant in asset mode;
  // irrelevant/hidden in template mode (onSaveOverride always writes back to
  // the template it was opened from, unconditionally).
  const [activeTemplateName, setActiveTemplateName] = useState<string | null>(null)
  const [alsoUpdateTemplate, setAlsoUpdateTemplate] = useState(false)

  const dragRef = useRef<{ startClientX: number; startClientY: number; startOffX: number; startOffY: number } | null>(null)

  interface SnapshotState {
    ratioKey: string
    bgPreset: BgPreset
    customColor: string
    gradients: BackgroundGradientConfig
    transform: SubjectTransform
  }
  const applySnapshot = (s: SnapshotState) => {
    setRatioKey(s.ratioKey)
    setBgPreset(s.bgPreset)
    setCustomColor(s.customColor)
    setGradients(s.gradients)
    setTransform(s.transform)
  }
  const snapshotFromParams = (params: BackgroundEditorParams | null | undefined): SnapshotState => {
    if (!params) {
      return { ratioKey: '1:1', bgPreset: 'white', customColor: '#ffffff', gradients: defaultGradients(), transform: DEFAULT_TRANSFORM }
    }
    return {
      ratioKey: params.ratioKey,
      bgPreset: params.bgPreset as BgPreset,
      // Normalize in case an older session persisted ColorPicker's
      // `toCssString()` form (`rgb(...)`) — reopen must not reintroduce the
      // blue-solid / black-gradient split.
      customColor: normalizeCssColorToHex(params.bgCustomColor || '#ffffff'),
      // Handles both the current composable model and legacy (pre-v3)
      // single-mode `bgMode`/`gradientIntensity` params transparently, so old
      // templates/chains keep looking the way they always did (spec 十.
      // 色底图旧数据) instead of silently resetting to solid.
      gradients: migrateBackgroundGradients(params),
      transform: params.transform,
    }
  }
  const SYSTEM_DEFAULT_SNAPSHOT: SnapshotState = {
    ratioKey: '1:1',
    bgPreset: 'white',
    customColor: '#ffffff',
    gradients: defaultGradients(),
    transform: DEFAULT_TRANSFORM,
  }
  const savedInitialRef = useRef<SnapshotState>(SYSTEM_DEFAULT_SNAPSHOT)

  // Restore the previously-saved editor state on open (if any), otherwise
  // fall back to sensible defaults. Either way this must never leave stale
  // state from a *different* chain's previous session lingering.
  useEffect(() => {
    if (!open) return
    const snap = snapshotFromParams(initialParams)
    savedInitialRef.current = snap
    applySnapshot(snap)
    setDirty(false)
    setSaveError('')
    setAlsoUpdateTemplate(false)
    setImgLoaded(false)
    setLoadError('')
    imgRef.current = null
    bboxRef.current = null
    fullResReadyRef.current = false
    fullResPromiseRef.current = null
    loadStartRef.current = performance.now()

    if (onSaveOverride) {
      // Template mode: `transparentUrl` is already a same-origin-to-backend
      // FileResponse (`serve_preview_file`), never R2-redirected, and there
      // is no `transparent_proxy` derivative for a template (no chain to
      // derive it from) — load it directly, exactly as before. Full-res by
      // construction, so no separate "wait for full-res" step is needed.
      loadHtmlImage(transparentUrl)
        .then((img) => {
          imgRef.current = img
          bboxRef.current = computeAlphaBBox(img)
          fullResReadyRef.current = true
          setImgLoaded(true)
        })
        .catch(() => setLoadError(t('backgroundEditor.loadFailed')))
      return
    }

    // Asset mode: fast `transparent_proxy` preview first (Editor Load
    // Performance P0, 2026-08) — interactive as soon as possible — then the
    // full-resolution `transparent` loads in the background and silently
    // upgrades `imgRef`/`bboxRef` (and re-draws) once ready. `doSave` always
    // awaits the full-res promise before exporting.
    const previewUrl = pixelsUrl(chainId, 'transparent_proxy')
    const fullResUrl = pixelsUrl(chainId, 'transparent')
    let cancelled = false
    // Preview Regression Forensic P0 (2026-08-29): same fix as
    // ShadowEditorModal — the fast preview and full-res loads race
    // independently, and the proxy derivative's on-demand cold-generation
    // can fail (confirmed live) while the full-res source loads fine right
    // after. Only declare a real, permanent failure once BOTH have failed.
    let previewFailed = false
    let fullResFailed = false
    const maybeReportFailure = () => {
      if (!cancelled && previewFailed && fullResFailed) setLoadError(t('backgroundEditor.loadFailed'))
    }

    loadHtmlImage(previewUrl)
      .then((img) => {
        if (cancelled) return
        const tDrawStart = performance.now()
        imgRef.current = img
        bboxRef.current = computeAlphaBBox(img)
        setImgLoaded(true)
        // `EDITOR_LOAD_TIMING` is logged here — at first-meaningful-preview
        // time, not after the background full-res upgrade too — since that
        // upgrade is explicitly non-blocking and must never count against
        // "how long until the user sees something".
        const timing = getLastPixelLoadTiming(previewUrl)
        logEditorLoadTiming({
          asset_id: chainId,
          editor_type: 'background',
          source_kind: 'transparent_proxy',
          source_url_type: 'same-origin-pixels-proxy',
          source_width: img.width,
          source_height: img.height,
          source_bytes: timing?.bytes ?? null,
          metadata_ms: null,
          image_ttfb_ms: timing?.ttfbMs ?? null,
          image_download_ms: timing?.downloadMs ?? null,
          decode_ms: timing?.decodeMs ?? null,
          canvas_init_ms: null,
          first_draw_ms: performance.now() - tDrawStart,
          total_ms: performance.now() - loadStartRef.current,
          deduped_request: timing?.deduped ?? false,
        })
      })
      .catch(() => {
        previewFailed = true
        maybeReportFailure()
      })

    const fullResPromise = loadHtmlImage(fullResUrl).then((img) => {
      if (!cancelled) {
        imgRef.current = img
        bboxRef.current = computeAlphaBBox(img)
        fullResReadyRef.current = true
        setFullResVersion((v) => v + 1)
        // Self-heal: the full-res source is authoritative and just proved
        // the real pixels ARE available, even if the fast preview above
        // had already failed — never leave the editor stuck on a stale
        // error once the actual image is in hand.
        setLoadError('')
        setImgLoaded(true)
      }
      return img
    })
    fullResPromiseRef.current = fullResPromise
    // Swallow here only to avoid a spurious "unhandled rejection" console
    // warning for this fire-and-forget background upgrade when the user
    // never saves — `doSave` above still awaits this exact same promise
    // object and sees the real rejection if a save is attempted while it's
    // still pending (a network failure loading the full-res source must
    // surface as a save error, never a silent low-res export).
    fullResPromise.catch(() => {
      fullResFailed = true
      maybeReportFailure()
    })

    // Reached only in asset mode (template mode returned above).
    getActiveAutoProcessTemplate()
      .then((r) => setActiveTemplateName(r.data.name))
      .catch(() => setActiveTemplateName(null))
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, chainId, transparentUrl])

  const markDirty = useCallback((updater: () => void) => {
    updater()
    setDirty(true)
  }, [])

  const draw = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const { w, h } = ratio
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w
      canvas.height = h
    }
    paintBackgroundGradients(ctx, w, h, baseColor, gradients)
    const img = imgRef.current
    const bbox = bboxRef.current
    if (img && bbox) {
      // "66%的最长边" (spec 四) is the SUBJECT's own longest side (its alpha
      // bbox), not the full transparent PNG — matting output is typically
      // padded to the original photo's canvas, so scaling/centering against
      // the padded image would render the subject smaller than intended and
      // off-center whenever that padding is asymmetric.
      const iw = img.naturalWidth || img.width
      const ih = img.naturalHeight || img.height
      const bw = Math.max(1, bbox.r - bbox.l)
      const bh = Math.max(1, bbox.b - bbox.t)
      const longSide = Math.max(w, h)
      const targetLong = longSide * (transform.scalePct / 100)
      const scale = targetLong / Math.max(bw, bh)
      const dw = iw * scale
      const dh = ih * scale
      const targetCx = w / 2 + transform.offsetXFrac * w
      const targetCy = h / 2 + transform.offsetYFrac * h
      const left = targetCx - ((bbox.l + bbox.r) / 2) * scale
      const top = targetCy - ((bbox.t + bbox.b) / 2) * scale
      ctx.drawImage(img, left, top, dw, dh)
    }
  }, [ratio, baseColor, gradients, transform])

  useEffect(() => {
    draw()
  }, [draw, imgLoaded, fullResVersion])

  // --- Drag to move (left mouse button) ---------------------------------
  const onCanvasPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (e.button !== 0) return
    ;(e.target as Element).setPointerCapture(e.pointerId)
    dragRef.current = {
      startClientX: e.clientX,
      startClientY: e.clientY,
      startOffX: transform.offsetXFrac,
      startOffY: transform.offsetYFrac,
    }
  }
  const onCanvasPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const drag = dragRef.current
    if (!drag) return
    const canvas = canvasRef.current
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    if (rect.width === 0 || rect.height === 0) return
    const dxFrac = (e.clientX - drag.startClientX) / rect.width
    const dyFrac = (e.clientY - drag.startClientY) / rect.height
    markDirty(() =>
      setTransform((t) => ({ ...t, offsetXFrac: drag.startOffX + dxFrac, offsetYFrac: drag.startOffY + dyFrac })),
    )
  }
  const onCanvasPointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (dragRef.current) {
      try {
        ;(e.target as Element).releasePointerCapture(e.pointerId)
      } catch {
        /* already released */
      }
    }
    dragRef.current = null
  }

  // --- Wheel to zoom -------------------------------------------------------
  // Native non-passive wheel listener, not a React synthetic `onWheel`
  // (VISTOERA_OBJECT_IMAGE_VIEWER_EDITOR_FULL_MODAL_ISOLATION_P0 section
  // 8/9): React's delegated wheel listener is attached at the root as
  // passive, which makes `preventDefault()` inside a plain `onWheel={...}`
  // handler unreliable across browsers — exactly the same class of bug
  // already fixed for `DualPaneCutoutEditor`. `preventDefault`/
  // `stopPropagation` run unconditionally, before any readiness check.
  useEffect(() => {
    const node = canvasRef.current
    if (!node) return
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault()
      e.stopPropagation()
      const step = clamp(-e.deltaY * 0.05, -8, 8)
      markDirty(() => setTransform((t) => ({ ...t, scalePct: clamp(t.scalePct + step, MIN_SCALE_PCT, MAX_SCALE_PCT) })))
    }
    node.addEventListener('wheel', handleWheel, { passive: false })
    return () => node.removeEventListener('wheel', handleWheel)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const updateAxis = (key: GradientAxisKey, patch: Partial<GradientAxisConfig>) => {
    markDirty(() => setGradients((g) => ({ ...g, [key]: { ...g[key], ...patch } })))
  }

  /** "纯色" (spec 三.3) — a shortcut action, not a stored mode: turns every
   * axis off but deliberately keeps each axis's `strength` untouched, so
   * re-enabling one afterwards restores whatever the user last dialed in. */
  const handleSolidShortcut = () => {
    markDirty(() =>
      setGradients((g) => ({
        radial: { ...g.radial, enabled: false },
        vertical: { ...g.vertical, enabled: false },
        horizontal: { ...g.horizontal, enabled: false },
      })),
    )
  }

  const handleRecenter = () => markDirty(() => setTransform((t) => ({ ...t, offsetXFrac: 0, offsetYFrac: 0 })))
  const handleResetScale = () => markDirty(() => setTransform((t) => ({ ...t, scalePct: DEFAULT_SCALE_PCT })))

  /** 取消: discard everything changed since the editor was opened. */
  const handleCancel = () => {
    applySnapshot(savedInitialRef.current)
    setDirty(false)
  }

  /** 恢复默认: reset to this editor's system defaults — only changes the
   * in-editor state; still requires "保存" to persist (spec 一.5). */
  const handleResetToSystemDefaults = () => {
    markDirty(() => applySnapshot(SYSTEM_DEFAULT_SNAPSHOT))
  }

  const requestClose = () => confirmDiscardAndClose(dirty && !saveSubmitted, onClose, t('editorHeader.discardConfirm'))

  const doSave = async () => {
    const canvas = canvasRef.current
    if (!canvas || saving || saveSubmitted) return
    setSaving(true)
    setSaveError('')
    try {
      // Preview optimization must never lower the exported file's quality
      // (Editor Load Performance P0, 2026-08) — if the full-resolution
      // source is still loading in the background, wait for it (and
      // re-render) before exporting, rather than shipping today's fast
      // `transparent_proxy`-resolution frame.
      if (!fullResReadyRef.current && fullResPromiseRef.current) {
        await fullResPromiseRef.current
        draw()
      }
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.95))
      if (!blob) throw new Error(t('backgroundEditor.exportFailed'))
      // Real editing state, not just what's currently displayed: every
      // field the canvas render actually consumed (spec 三.3). Only the
      // current composable `gradients` structure is written — the legacy
      // `bgMode`/`gradientIntensity`/`gradientVersion` fields are never
      // written by this editor again once saved (spec 十: 保存后使用新的组合
      // 结构), though older un-migrated data on disk keeps reading fine via
      // `migrateBackgroundGradients`.
      const params: BackgroundEditorParams = {
        ratioKey,
        bgPreset,
        // Persist canonical hex so backend synthesis / reopen never see
        // ColorPicker's `rgb(...)` CSS form.
        bgCustomColor: normalizeCssColorToHex(customColor),
        gradients,
        transform,
      }
      if (onSaveOverride) {
        // Template mode: always writes back to the same template's
        // 透明底图→色底图 stage config it was opened from — there is no
        // separate "asset" to protect here, so no opt-in checkbox needed.
        await onSaveOverride(blob, params)
        setDirty(false)
        message.success(t('backgroundEditor.saved'))
        onClose()
      } else {
        // Hand upload+persist to the module-level save manager so the task
        // survives this editor closing immediately (Async Save P0).
        const accepted = submitEditorSave({
          kind: 'background',
          chainId,
          run: async () => {
            const { data } = await backgroundEditorSave(chainId, blob, params)
            return data
          },
          onSaved: (data) => onSaved?.(data),
          successMessage: alsoUpdateTemplate
            ? t('backgroundEditor.savedAndSynced')
            : t('backgroundEditor.savedRegeneratingShadow'),
        })
        if (accepted) {
          setSaveSubmitted(true)
          setDirty(false)
          // Template sync is best-effort and strictly secondary to the asset
          // save already handed off above (spec 十七).
          if (alsoUpdateTemplate) {
            syncStageToActiveTemplate('background', params as unknown as Record<string, unknown>).catch(() => undefined)
          }
          onClose()
        }
      }
    } catch (e) {
      const msg = (e as Error).message || t('backgroundEditor.saveFailed')
      setSaveError(msg)
      message.error(msg)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      onCancel={requestClose}
      footer={null}
      closable={false}
      width="90vw"
      centered
      style={{ paddingBottom: 0 }}
      styles={{ body: { height: '88vh', padding: 0 }, mask: { backgroundColor: 'transparent' } }}
      wrapClassName={MODAL_SURFACE_CLASS}
      destroyOnClose
    >
      <div className="bgedit-shell" data-testid="background-editor-modal">
        <EditorHeader
          testIdPrefix="bgedit"
          title={`${titlePrefix || t('backgroundEditor.title')} · ${filename}`}
          dirty={dirty && !saveSubmitted}
          saving={saving || saveSubmitted}
          saveError={saveError}
          saveDisabled={!imgLoaded}
          onCancel={handleCancel}
          onReset={handleResetToSystemDefaults}
          onSave={doSave}
          onClose={requestClose}
          extra={
            !onSaveOverride && activeTemplateName ? (
              <Tooltip title={t('backgroundEditor.syncToTemplateTooltip', { name: activeTemplateName })}>
                <Checkbox
                  className="edhdr-sync-template-check"
                  checked={alsoUpdateTemplate}
                  onChange={(e) => setAlsoUpdateTemplate(e.target.checked)}
                  data-testid="bgedit-sync-template-checkbox"
                >
                  {t('backgroundEditor.syncToTemplate')}
                </Checkbox>
              </Tooltip>
            ) : undefined
          }
        />

        <div className="bgedit-body">
          <aside className="bgedit-panel">
            <BgEditGroup title={t('backgroundEditor.canvas')}>
              <div className="bgedit-field-label">{t('backgroundEditor.canvasRatio')}</div>
              <div className="bgedit-ratio-grid">
                {RATIO_PRESETS.map((r) => (
                  <button
                    key={r.key}
                    type="button"
                    data-testid={`bgedit-ratio-${r.key}`}
                    className={`bgedit-chip${ratioKey === r.key ? ' active' : ''}`}
                    onClick={() => markDirty(() => setRatioKey(r.key))}
                  >
                    <span className={`bgedit-ratio-swatch bgedit-ratio-swatch-${r.key.replace(':', '-')}`} />
                    {t(`backgroundEditor.ratioPresets.${r.key}`, { defaultValue: r.label })}
                  </button>
                ))}
              </div>
              <div className="bgedit-hint" data-testid="bgedit-output-size">
                {t('backgroundEditor.outputSize', { w: ratio.w, h: ratio.h })}
              </div>
            </BgEditGroup>

            <BgEditGroup title={t('backgroundEditor.background')}>
              <div className="bgedit-field-label">{t('backgroundEditor.backgroundType')}</div>
              <div className="bgedit-swatch-row">
                {(Object.keys(BG_PRESET_COLOR) as Array<Exclude<BgPreset, 'custom'>>).map((key) => (
                  <Tooltip key={key} title={t(`backgroundEditor.bgPresets.${key}`, { defaultValue: BG_PRESET_LABEL[key] })}>
                    <button
                      type="button"
                      data-testid={`bgedit-swatch-${key}`}
                      className={`bgedit-swatch${bgPreset === key ? ' active' : ''}`}
                      style={{ background: BG_PRESET_COLOR[key] }}
                      onClick={() => markDirty(() => setBgPreset(key))}
                      aria-label={t(`backgroundEditor.bgPresets.${key}`, { defaultValue: BG_PRESET_LABEL[key] })}
                    />
                  </Tooltip>
                ))}
                <ColorPicker
                  value={customColor}
                  disabledAlpha
                  format="hex"
                  onChange={(color, hex) => markDirty(() => {
                    // EDITOR-RESULT-FIX-A1 (Bug B, custom-color path) +
                    // BG-GRADIENT-FIX-A1: antd's ColorPicker `onChange` second
                    // param is documented as a hex string but can actually be
                    // an `rgb(r, g, b)` CSS string depending on the popover's
                    // format toggle — `format="hex"` pins the popover's own
                    // toggle to hex (belt), and normalizing through
                    // `hexToRgb`/`rgbToHex` (which both accept `rgb(...)` too)
                    // guarantees a real `#rrggbb` reaches `customColor` even
                    // if either the popover toggle or `color.toHexString()`
                    // ever hands back something else (suspenders). Every
                    // downstream consumer of `customColor` (this editor's own
                    // gradient renderer, the Shadow Editor's inherited-
                    // background preview, and the persisted `bgCustomColor`
                    // param the backend re-renders from) only ever sees a
                    // real hex string going forward.
                    setCustomColor(rgbToHex(...hexToRgb(color.toHexString() || hex)))
                    setBgPreset('custom')
                  })}
                >
                  <Tooltip title={t('backgroundEditor.customColor')}>
                    <button
                      type="button"
                      data-testid="bgedit-swatch-custom"
                      className={`bgedit-swatch bgedit-swatch-custom${bgPreset === 'custom' ? ' active' : ''}`}
                      style={{ background: bgPreset === 'custom' ? customColor : undefined }}
                      aria-label={t('backgroundEditor.customColor')}
                    />
                  </Tooltip>
                </ColorPicker>
              </div>

              <div className="bgedit-field-label bgedit-gradients-head" style={{ marginTop: 4 }}>
                <span>{t('backgroundEditor.backgroundEffects')}</span>
                <button
                  type="button"
                  className="bgedit-solid-shortcut-btn"
                  data-testid="bgedit-solid-shortcut-btn"
                  onClick={handleSolidShortcut}
                  disabled={isSolidGradients(gradients)}
                  title={t('backgroundEditor.solidOnlyTooltip')}
                >
                  {t('backgroundEditor.solidOnly')}
                </button>
              </div>
              {isSolidGradients(gradients) && (
                <div className="bgedit-hint" data-testid="bgedit-solid-hint">
                  {t('backgroundEditor.noGradientSolid')}
                </div>
              )}
              <div className="bgedit-gradient-axis-list" data-testid="bgedit-gradient-axis-list">
                {GRADIENT_AXIS_ORDER.map((key) => {
                  const axis = gradients[key]
                  const captions = t(`backgroundEditor.gradientCaption.${key}`, { returnObjects: true }) as string[]
                  const [negLabel, posLabel] = Array.isArray(captions) && captions.length >= 2 ? captions : ['', '']
                  return (
                    <div
                      key={key}
                      className={`bgedit-gradient-axis${axis.enabled ? ' active' : ''}`}
                      data-testid={`bgedit-gradient-axis-${key}`}
                    >
                      <label className="bgedit-gradient-axis-toggle">
                        <Checkbox
                          checked={axis.enabled}
                          onChange={(e) => updateAxis(key, { enabled: e.target.checked })}
                          data-testid={`bgedit-gradient-axis-${key}-checkbox`}
                        />
                        <span>{t(`backgroundEditor.gradientAxis.${key}`)}</span>
                        <span className="bgedit-gradient-axis-value" data-testid={`bgedit-gradient-axis-${key}-value`}>
                          {axis.strength === 0 ? '0' : `${axis.strength > 0 ? '+' : ''}${axis.strength}%`}
                        </span>
                      </label>
                      {axis.enabled && (
                        <>
                          <div className="bgedit-gradient-slider-wrap" data-testid={`bgedit-gradient-axis-${key}-slider`}>
                            <Slider
                              min={-100}
                              max={100}
                              value={axis.strength}
                              marks={{ '-100': '−', 0: '0', '100': '+' }}
                              onChange={(v) => updateAxis(key, { strength: v as number })}
                            />
                          </div>
                          <div className="bgedit-gradient-scale-caption">
                            <span>{negLabel}</span>
                            <span>0</span>
                            <span>{posLabel}</span>
                          </div>
                        </>
                      )}
                    </div>
                  )
                })}
              </div>
            </BgEditGroup>

            <BgEditGroup title={t('backgroundEditor.composition')}>
              <div className="bgedit-slider-row" data-testid="bgedit-scale-row">
                <div className="bgedit-slider-head">
                  <span>{t('backgroundEditor.scale')}</span>
                  <span data-testid="bgedit-scale-value">{Math.round(transform.scalePct)}%</span>
                </div>
                <Slider
                  min={MIN_SCALE_PCT}
                  max={MAX_SCALE_PCT}
                  value={transform.scalePct}
                  onChange={(v) => markDirty(() => setTransform((prev) => ({ ...prev, scalePct: v })))}
                />
              </div>
              <div className="bgedit-hint">{t('backgroundEditor.dragHint')}</div>
              <div className="bgedit-btn-row">
                <Button
                  size="small"
                  icon={<AimOutlined />}
                  onClick={handleRecenter}
                  className="bgedit-round-btn"
                  data-testid="bgedit-recenter-btn"
                  block
                >
                  {t('backgroundEditor.center')}
                </Button>
                <Button
                  size="small"
                  onClick={handleResetScale}
                  className="bgedit-round-btn"
                  data-testid="bgedit-reset-scale-btn"
                  block
                >
                  {t('backgroundEditor.restoreDefaultSize')}
                </Button>
              </div>
            </BgEditGroup>
          </aside>

          <div className="bgedit-preview">
            <div className="bgedit-canvas-stage">
              <canvas
                ref={canvasRef}
                className="bgedit-canvas"
                data-testid="bgedit-canvas"
                onPointerDown={onCanvasPointerDown}
                onPointerMove={onCanvasPointerMove}
                onPointerUp={onCanvasPointerUp}
                onPointerCancel={onCanvasPointerUp}
              />
              {/* Composition safe-area guide — a pure DOM overlay, never
                  drawn onto the canvas, so it can never leak into the
                  exported JPEG regardless of how save() is implemented. */}
              <div className="bgedit-safe-area" data-testid="bgedit-safe-area" />
              {!imgLoaded && !loadError && (
                <div className="bgedit-loading" data-testid="bgedit-loading">
                  {t('backgroundEditor.loadingTransparent')}
                </div>
              )}
              {loadError && (
                <div className="bgedit-loading bgedit-loading-error" data-testid="bgedit-load-error">
                  {loadError}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}

function BgEditGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="bgedit-group">
      <div className="bgedit-group-title">{title}</div>
      {children}
    </section>
  )
}
